import { useMemo, useState } from "react";
import { useLoaderData, useNavigate, useNavigation, useSubmit } from "react-router-dom";

import { useAuthStore } from "src/components/main/authStore";
import {
    buildWeekNumArray,
    getDateEndFromWeekNum,
    getDateEndFromWeekNum2,
    getDateTime,
    getDatefromWeekNum,
    MyDate,
    standbyStartDate,
    weekDays,
    weeknum,
} from "src/components/common/functions";

type CheckInDay = {
    morning: boolean;
    afternoon: boolean;
    doOT: string;
    otDate: string;
    otTime: string;
    unlock: boolean;
};

type CheckInFormData = {
    form: CheckInDay[];
    status?: string;
};

type LoaderData = {
    week: string;
    form: CheckInFormData;
};

const title = "CheckIn";

export default function CheckIn() {
    const { week, form: loadedForm } = useLoaderData() as LoaderData;
    const user = useAuthStore(state => state.user);
    const navigate = useNavigate();
    const navigation = useNavigation();
    const submit = useSubmit();
    const [form, setForm] = useState<CheckInFormData>(loadedForm);
    const [isEditingContent, setIsEditingContent] = useState(false);
    const isSubmitting = navigation.state === "submitting";
    const hireDateString = user?.hireDate ?? "";
    const quitDateString = user?.quitDate ?? "";

    const weeks = useMemo(
        () => buildWeekNumArray(standbyStartDate).filter((value) => (
            getDateTime(value, 7, 0, 0) > new Date(hireDateString.replace(/-/g, "/")) &&
            (quitDateString ? getDateTime(value, 0, 0, 0) <= new Date(quitDateString.replace(/-/g, "/")) : true)
        )).reverse(),
        [hireDateString, quitDateString]
    );
    const activeWeek = weeks.includes(week)
        ? week
        : (weeks.includes(weeknum(new MyDate().s())) ? weeknum(new MyDate().s()) : (weeks[0] || ""));

    const timeValid1 = (weekValue: string, index: number) => getDateTime(weekValue, index, 10, 30) > new Date();
    const timeValid2 = (weekValue: string, index: number) => getDateTime(weekValue, index, 13, 30) > new Date();
    const activeWeekEnd = getDateEndFromWeekNum2(activeWeek);
    const canEdit = activeWeekEnd ? new Date(activeWeekEnd) >= new Date() : false;

    const updateDay = (dayIndex: number, updater: (day: CheckInDay) => CheckInDay) => {
        setForm((currentForm) => ({
            ...currentForm,
            form: currentForm.form.map((day, index) => index === dayIndex ? updater(day) : day),
        }));
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "checkInWeek">Week</label>
                    <select
                        id = "checkInWeek"
                        className = "form-control w-auto"
                        value = {activeWeek}
                        onChange = {e => {
                            setIsEditingContent(false);
                            navigate(`/${title}/${e.currentTarget.value}`);
                        }}
                    >
                        {weeks.map((value, index) => <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                    </select>
                </div>
            </nav>
            <form
                className = "p-3"
                onSubmit = {e => {
                    e.preventDefault();
                    submit(new FormData(e.currentTarget), { method: "post" });
                    setIsEditingContent(false);
                }}
            >
                <input type = "hidden" name = "currentWeek" value = {activeWeek} />
                <input type = "hidden" name = "data" value = {JSON.stringify(form)} />
                <div className = "h3 text-danger fw-bold">{getDateEndFromWeekNum(activeWeek)}</div>
                <li>
                    <strong><u>Instructions</u></strong>
                    <ul>
                        <li>The purpose of this check-in is to make sure that you are safe. Please do not check both boxes at once. The point is to check them out throughout the day. First cutoff is at 10:30am and the second cutoff is at 1:30pm. If you will be working overtime, please inform us with the second check in and let us know what your expected end time will be.</li>
                        <li>We know this can be tedious, but we do not want anything to happen to you. It's all about <strong><u>your</u></strong> safety! Remember YNY!!</li>
                        <li>Thank you!</li>
                    </ul>
                </li>
                <strong><u>Current Danger: HEAT</u></strong>
                <p><em>Extreme heat causes more deaths each year than hurricanes, lightning, tornadoes, earthquakes, and flood COMBINED.</em></p>
                <li>
                    <strong><u>Who is at risk</u></strong>
                    <ul>
                        <li>Adults over 65</li>
                        <li>Children under 4</li>
                        <li>Those with existing medical problems such as heart disease.</li>
                    </ul>
                </li>
                <li>
                    <strong><u>What to do</u></strong>
                    <ul>
                        <li>Stay Cool</li>
                        <ul>
                            <li>Find air conditioning</li>
                            <li>Avoid direct sunlight</li>
                            <li>Weart lightweight, light-colored clothes</li>
                            <li>Use a fan as a primary cooling device</li>
                            <li>Take cool showers</li>
                            <li>Use Ice Packs on your body in no more than 15 min increments, if possible, and always use a towel or cloth between your skin and the Ice Pack.</li>
                        </ul>
                        <li>Stay Hydrated</li>
                        <ul>
                            <li>Drink more water than usual</li>
                            <li>Start hydrating the night before</li>
                            <li>Don't wait until you are thirsty to drink more fluids</li>
                            <li>Avoid alcohol and liquids containing high sugar content</li>
                        </ul>
                    </ul>
                </li>
                <li>
                    <strong><u>What to look for</u></strong>
                    <ul>
                        <li>Heat Exhaustion</li>
                        <ul>
                            <li>Dizziness / Light-headed</li>
                            <li>Heavy sweating & thirst</li>
                            <li>Nausea / Vomiting</li>
                            <li>Muscle Cramps</li>
                            <li>Weakness</li>
                        </ul>
                        <li>Heat Stroke</li>
                        <ul>
                            <li>Elevated temperature</li>
                            <li>Confusion/ strange behavior</li>
                            <li>Difficulty breathing</li>
                            <li>Persistent vomiting</li>
                            <li>Loss of consciousness</li>
                        </ul>
                    </ul>
                </li>
                <div className = "row">
                    {weekDays.map((weekday, index) => (
                        <div className = "col-lg" key = {index}>
                            <div className = "mt-2 h4 text-secondary">{weekday} ({getDatefromWeekNum(activeWeek, index)})</div>
                            <div className = "mt-2">
                                {!timeValid1(activeWeek, index) && !form.form[index].unlock ? <span className = "badge badge-primary">past</span> : null}
                            </div>
                            <div className = "mt-2">
                                <input
                                    id = {`acknowledge-${index}-1`}
                                    className = "cssCheckBox mt-2"
                                    type = "checkbox"
                                    disabled = {!(isEditingContent && (timeValid1(activeWeek, index) || form.form[index].unlock))}
                                    checked = {form.form[index].morning}
                                    onChange = {e => {
                                        const checked = e.currentTarget.checked;
                                        updateDay(index, (day) => ({ ...day, morning: checked }));
                                    }}
                                />
                                <label htmlFor = {`acknowledge-${index}-1`} className = "ms-2">I acknowledge that I understand the dangers of high temperatures and that I am currently safe.</label>
                            </div>
                            <div className = "mt-2">
                                {!timeValid2(activeWeek, index) && !form.form[index].unlock ? <span className = "badge badge-primary mt-2">past</span> : null}
                            </div>
                            <div className = "mt-2">
                                <input
                                    id = {`acknowledge-${index}-2`}
                                    className = "cssCheckBox mt-2"
                                    type = "checkbox"
                                    disabled = {!(isEditingContent && (timeValid2(activeWeek, index) || form.form[index].unlock))}
                                    checked = {form.form[index].afternoon}
                                    onChange = {e => {
                                        const checked = e.currentTarget.checked;
                                        updateDay(index, (day) => ({ ...day, afternoon: checked }));
                                    }}
                                />
                                <label htmlFor = {`acknowledge-${index}-2`} className = "ms-2">I acknowledge that I understand the dangers of high temperatures and that I am currently safe.</label>
                            </div>
                            <div className = "mt-2">
                                <label htmlFor = {`ot-${index}`} className = "fw-bold mb-0">Are you planning to do OT?</label>
                                {isEditingContent && (timeValid2(activeWeek, index) || form.form[index].unlock) ? (
                                    <select
                                        id = {`ot-${index}`}
                                        className = "form-control"
                                        value = {form.form[index].doOT}
                                        onChange = {e => {
                                            const value = e.currentTarget.value;
                                            updateDay(index, (day) => ({
                                                ...day,
                                                doOT: value,
                                                otDate: "",
                                                otTime: "",
                                            }));
                                        }}
                                    >
                                        <option value = "">Select</option>
                                        <option>Yes</option>
                                        <option>No</option>
                                    </select>
                                ) : <span className = "ms-2">{form.form[index].doOT}</span>}
                            </div>
                            {form.form[index].doOT === "Yes" ? (
                                <>
                                    <label htmlFor = {`endDate-${index}`} className = "fw-bold mb-0 mt-2">Estimated End Date</label>
                                    {isEditingContent && (timeValid2(activeWeek, index) || form.form[index].unlock) ? (
                                        <input
                                            className = "form-control"
                                            id = {`endDate-${index}`}
                                            required
                                            type = "date"
                                            value = {form.form[index].otDate}
                                            onChange = {e => {
                                                const value = e.currentTarget.value;
                                                updateDay(index, (day) => ({ ...day, otDate: value }));
                                            }}
                                        />
                                    ) : <span className = "ms-2">{form.form[index].otDate}</span>}
                                    <label htmlFor = {`endTime-${index}`} className = "fw-bold mb-0 mt-2">Estimated End Time</label>
                                    {isEditingContent && (timeValid2(activeWeek, index) || form.form[index].unlock) ? (
                                        <input
                                            className = "form-control"
                                            id = {`endTime-${index}`}
                                            required
                                            type = "time"
                                            value = {form.form[index].otTime}
                                            onChange = {e => {
                                                const value = e.currentTarget.value;
                                                updateDay(index, (day) => ({ ...day, otTime: value }));
                                            }}
                                        />
                                    ) : <span className = "ms-2">{form.form[index].otTime}</span>}
                                </>
                            ) : null}
                        </div>
                    ))}
                </div>
                {canEdit ? (
                    <div className = "mt-2 text-right">
                        {isEditingContent ? (
                            <button key = "save" type = "submit" value = "Submit" className = "btn btn-primary" disabled = {isSubmitting}>
                                {isSubmitting ? "Saving..." : "Save Content"}
                            </button>
                        ) : (
                            <button key = "edit" type = "button" className = "btn btn-primary" onClick = {e => {
                                e.preventDefault();
                                setIsEditingContent(true);
                            }}>
                                Edit Content
                            </button>
                        )}
                    </div>
                ) : null}
            </form>
        </div>
    );
}
